import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $applyNodeReplacement, type LexicalNode, type NodeKey, type SerializedLexicalNode, type Spread } from "lexical";
import { ArrowRight, CheckCircle, FileText } from "lucide-react";
import { JSX, useEffect, useRef, useState } from "react";
import { ContractRecommendation } from "@/components/Editor/ContractRecommendation";
import { ContractSearch } from "@/components/Editor/ContractSearch";
import { NodeDeleteButton } from "@/components/Editor/NodeDeleteButton";
import { useDebouncedUpdate } from "@/components/Editor/useDebouncedUpdate";
import { TickerAutoComplete } from "@/components/TickerAutoComplete";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DECISION_ACTION_TYPE, DECISION_TYPE, type DecisionActionType, type DecisionType } from "@/constants/lexical";
import { cn } from "@/lib/utils";
import type { SelfContract } from "@/types/proto/api/v1/memo_service_pb";
import { useTranslate } from "@/utils/i18n";
import { BaseAppNode, registerBaseAppNodeType } from "./BaseAppNode";
import { WindowDecorator } from "./WindowNode";

// Register this node type with BaseAppNode
registerBaseAppNodeType("decision");

// =============================================================================
// Type Definitions
// =============================================================================

export type SerializedDecisionNode = Spread<
  {
    type: "decision";
    version: 1;
    key: string;
    ticker: string;
    contractId?: string;
    decision: DecisionType;
    actionType: DecisionActionType;
    price: number;
    quantity: number;
    reason: string;
    executedAt: string;
    createdAt?: string;
    confidence?: number;
    riskLevel?: string;
    timeHorizon?: string;
  },
  SerializedLexicalNode
>;

/**
 * DecisionData - Decision node data (without Lexical metadata)
 */
export type DecisionData = Omit<SerializedDecisionNode, keyof SerializedLexicalNode>;

// =============================================================================
// DecisionNode
// =============================================================================

/**
 * DecisionNode - Decision 决策节点 (统一的事后决策节点)
 *
 * Per architecture: Node holds value fields directly. Content Schema is SSOT via Adapter.
 */
export class DecisionNode extends BaseAppNode {
  __ticker: string;
  __contractId?: string;
  __decision: DecisionType;
  __actionType: DecisionActionType;
  __price: number;
  __quantity: number;
  __reason: string;
  __executedAt: string;
  __createdAt?: string;
  __confidence?: number;
  __riskLevel?: string;
  __timeHorizon?: string;

  static getType(): string {
    return "decision";
  }

  static clone(node: DecisionNode): DecisionNode {
    return new DecisionNode(
      node.__ticker,
      node.__contractId,
      node.__decision,
      node.__actionType,
      node.__price,
      node.__quantity,
      node.__reason,
      node.__executedAt,
      node.__createdAt,
      node.__confidence,
      node.__riskLevel,
      node.__timeHorizon,
      node.__key,
    );
  }

  constructor(
    ticker?: string,
    contractId?: string,
    decision?: DecisionType,
    actionType?: DecisionActionType,
    price?: number,
    quantity?: number,
    reason?: string,
    executedAt?: string,
    createdAt?: string,
    confidence?: number,
    riskLevel?: string,
    timeHorizon?: string,
    key?: NodeKey,
  ) {
    super(false, key); // false = block-level node
    this.__ticker = ticker ?? "";
    this.__contractId = contractId;
    this.__decision = decision ?? DECISION_TYPE.BUY;
    this.__actionType = actionType ?? DECISION_ACTION_TYPE.NONE;
    this.__price = price ?? 0;
    this.__quantity = quantity ?? 0;
    this.__reason = reason ?? "";
    this.__executedAt = executedAt ?? "";
    this.__createdAt = createdAt;
    this.__confidence = confidence;
    this.__riskLevel = riskLevel;
    this.__timeHorizon = timeHorizon;
  }

  // ========== DOM 操作 ==========

  createDOM(): HTMLElement {
    const div = document.createElement("div");
    div.className = "decision-node-container";
    div.setAttribute("data-lexical-decorator", "true");
    return div;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    return <DecisionDecorator node={this} />;
  }

  // ========== 序列化 ==========

  static importJSON(serializedNode: SerializedDecisionNode): DecisionNode {
    return $createDecisionNode({
      ticker: serializedNode.ticker,
      contractId: serializedNode.contractId,
      decision: serializedNode.decision,
      actionType: serializedNode.actionType,
      price: serializedNode.price,
      quantity: serializedNode.quantity,
      reason: serializedNode.reason,
      executedAt: serializedNode.executedAt,
      createdAt: serializedNode.createdAt,
      confidence: serializedNode.confidence,
      riskLevel: serializedNode.riskLevel,
      timeHorizon: serializedNode.timeHorizon,
    });
  }

  exportJSON(): SerializedDecisionNode {
    return {
      type: "decision",
      version: 1,
      key: this.__key,
      ticker: this.__ticker,
      contractId: this.__contractId,
      decision: this.__decision,
      actionType: this.__actionType,
      price: this.__price,
      quantity: this.__quantity,
      reason: this.__reason,
      executedAt: this.__executedAt,
      createdAt: this.__createdAt,
      confidence: this.__confidence,
      riskLevel: this.__riskLevel,
      timeHorizon: this.__timeHorizon,
    };
  }

  // ========== Getter/Setter ==========

  getTicker(): string {
    return this.__ticker;
  }

  setTicker(ticker: string): void {
    const writable = this.getWritable();
    writable.__ticker = ticker;
  }

  getContractId(): string {
    return this.__contractId || "";
  }

  setContractId(contractId: string): void {
    const writable = this.getWritable();
    writable.__contractId = contractId;
  }

  getDecision(): DecisionType {
    return this.__decision;
  }

  setDecision(decision: DecisionType): void {
    const writable = this.getWritable();
    writable.__decision = decision;
  }

  getActionType(): DecisionActionType {
    return this.__actionType;
  }

  setActionType(actionType: DecisionActionType): void {
    const writable = this.getWritable();
    writable.__actionType = actionType;
  }

  getPrice(): number {
    return this.__price;
  }

  setPrice(price: number): void {
    const writable = this.getWritable();
    writable.__price = price;
  }

  getQuantity(): number {
    return this.__quantity;
  }

  setQuantity(quantity: number): void {
    const writable = this.getWritable();
    writable.__quantity = quantity;
  }

  getReason(): string {
    return this.__reason;
  }

  setReason(reason: string): void {
    const writable = this.getWritable();
    writable.__reason = reason;
  }

  getExecutedAt(): string {
    return this.__executedAt;
  }

  setExecutedAt(executedAt: string): void {
    const writable = this.getWritable();
    writable.__executedAt = executedAt;
  }

  getCreatedAt(): string {
    return this.__createdAt || "";
  }

  getConfidence(): number | undefined {
    return this.__confidence;
  }

  setConfidence(confidence: number | undefined): void {
    const writable = this.getWritable();
    writable.__confidence = confidence;
  }

  getRiskLevel(): string | undefined {
    return this.__riskLevel;
  }

  setRiskLevel(riskLevel: string | undefined): void {
    const writable = this.getWritable();
    writable.__riskLevel = riskLevel;
  }

  getTimeHorizon(): string | undefined {
    return this.__timeHorizon;
  }

  setTimeHorizon(timeHorizon: string | undefined): void {
    const writable = this.getWritable();
    writable.__timeHorizon = timeHorizon;
  }

  // ========== 其他 ==========

  getTextContent(): string {
    const decisionLabel = this.getDecisionLabel();
    const actionLabel = this.getActionLabel();
    return `[决策] $${this.__ticker} ${decisionLabel} ${actionLabel} | ${this.__price} x${this.__quantity} | ${this.__reason}`;
  }

  isEqualTo(other: LexicalNode): boolean {
    if (!(other instanceof DecisionNode)) {
      return false;
    }
    return (
      this.__ticker === other.__ticker &&
      this.__decision === other.__decision &&
      this.__actionType === other.__actionType &&
      this.__executedAt === other.__executedAt
    );
  }

  // ========== 辅助方法 ==========

  getDecisionLabel(): string {
    switch (this.__decision) {
      case DECISION_TYPE.BUY:
        return "买入";
      case DECISION_TYPE.SELL:
        return "卖出";
      case DECISION_TYPE.HOLD:
        return "持有";
      default:
        return "未知";
    }
  }

  getActionLabel(): string {
    switch (this.__actionType) {
      case DECISION_ACTION_TYPE.FOLLOW:
        return "遵守契约";
      case DECISION_ACTION_TYPE.OVERRIDE:
        return "违背契约";
      case DECISION_ACTION_TYPE.NONE:
        return "独立决策";
      default:
        return "未知";
    }
  }
}

// =============================================================================
// Decorator Component
// =============================================================================

/**
 * Decision 节点编辑器组件
 */
function DecisionDecorator({ node }: { node: DecisionNode }) {
  const t = useTranslate();
  const [editor] = useLexicalComposerContext();
  const decoratorRef = useRef<HTMLDivElement>(null);

  // Local state for all form fields
  const [ticker, setTicker] = useState(node.getTicker());
  const [contractId, setContractId] = useState(node.getContractId());
  const [contractName, setContractName] = useState("");
  const [decision, setDecision] = useState(node.getDecision());
  const [actionType, setActionType] = useState(node.getActionType());
  const [price, setPrice] = useState(node.getPrice());
  const [quantity, setQuantity] = useState(node.getQuantity());
  const [reason, setReason] = useState(node.getReason());
  const [executedAt, setExecutedAt] = useState(node.getExecutedAt());

  // Contract recommendation/search state
  const [showContractRecommendation, setShowContractRecommendation] = useState(false);
  const [showContractSearch, setShowContractSearch] = useState(false);

  const prevValuesRef = useRef({
    ticker,
    contractId,
    decision,
    actionType,
    price,
    quantity,
    reason,
    executedAt,
  });

  useEffect(() => {
    setTicker(node.getTicker());
    setContractId(node.getContractId());
    setDecision(node.getDecision());
    setActionType(node.getActionType());
    setPrice(node.getPrice());
    setQuantity(node.getQuantity());
    setReason(node.getReason());
    setExecutedAt(node.getExecutedAt());
    prevValuesRef.current = {
      ticker: node.getTicker(),
      contractId: node.getContractId(),
      decision: node.getDecision(),
      actionType: node.getActionType(),
      price: node.getPrice(),
      quantity: node.getQuantity(),
      reason: node.getReason(),
      executedAt: node.getExecutedAt(),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced update functions
  const updateTicker = useDebouncedUpdate(
    (value: string) => {
      if (value !== prevValuesRef.current.ticker) {
        node.setTicker(value);
        prevValuesRef.current.ticker = value;
      }
    },
    () => editor,
    300,
  );

  const updateContractId = useDebouncedUpdate(
    (value: string) => {
      if (value !== prevValuesRef.current.contractId) {
        node.setContractId(value);
        prevValuesRef.current.contractId = value;
      }
    },
    () => editor,
    300,
  );

  const updatePrice = useDebouncedUpdate(
    (value: number) => {
      if (value !== prevValuesRef.current.price) {
        node.setPrice(value);
        prevValuesRef.current.price = value;
      }
    },
    () => editor,
    300,
  );

  const updateQuantity = useDebouncedUpdate(
    (value: number) => {
      if (value !== prevValuesRef.current.quantity) {
        node.setQuantity(value);
        prevValuesRef.current.quantity = value;
      }
    },
    () => editor,
    300,
  );

  const updateReason = useDebouncedUpdate(
    (value: string) => {
      if (value !== prevValuesRef.current.reason) {
        node.setReason(value);
        prevValuesRef.current.reason = value;
      }
    },
    () => editor,
    300,
  );

  const updateExecutedAt = useDebouncedUpdate(
    (value: string) => {
      if (value !== prevValuesRef.current.executedAt) {
        node.setExecutedAt(value);
        prevValuesRef.current.executedAt = value;
      }
    },
    () => editor,
    300,
  );

  const handleTickerChange = (value: string) => {
    const uppercaseValue = value.toUpperCase();
    setTicker(uppercaseValue);
    updateTicker(uppercaseValue);

    if (uppercaseValue && uppercaseValue.length >= 2) {
      setShowContractRecommendation(true);
    } else {
      setShowContractRecommendation(false);
    }
  };

  const handleTickerSelect = (symbol: string) => {
    handleTickerChange(symbol);
  };

  const handleSelectContract = (contract: SelfContract) => {
    const newContractId = contract.contractId;
    setContractId(newContractId);
    setContractName(`${contract.template} - ${formatDate(contract.expiration)}`);
    setShowContractRecommendation(false);
    setShowContractSearch(false);

    if (actionType === DECISION_ACTION_TYPE.NONE) {
      setActionType(DECISION_ACTION_TYPE.FOLLOW);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          editor.update(() => {
            node.setActionType(DECISION_ACTION_TYPE.FOLLOW);
            prevValuesRef.current.actionType = DECISION_ACTION_TYPE.FOLLOW;
          });
        });
      });
    }

    updateContractId(newContractId);
  };

  const handleSkipRecommendation = () => {
    setShowContractRecommendation(false);
  };

  const handleOpenContractSearch = () => {
    setShowContractRecommendation(false);
    setShowContractSearch(true);
  };

  const handleCloseContractSearch = () => {
    setShowContractSearch(false);
  };

  const handleClearContract = () => {
    setContractId("");
    setContractName("");
    updateContractId("");

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        editor.update(() => {
          node.setActionType(DECISION_ACTION_TYPE.NONE);
          prevValuesRef.current.actionType = DECISION_ACTION_TYPE.NONE;
        });
      });
    });
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
    } catch {
      return dateString;
    }
  };

  return (
    <WindowDecorator node={node} decoratorRef={decoratorRef} className="my-4 rounded-lg border bg-card shadow-sm relative z-0 w-full group">
      {(handleDelete, decoratorRef) => (
        <div ref={decoratorRef}>
          {/* Header: Ticker + Decision + Action + Delete */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
            <div className="flex items-center gap-3">
              <TickerAutoComplete
                value={ticker}
                onChange={handleTickerChange}
                onSelect={handleTickerSelect}
                placeholder=""
                className="w-36"
                inputClassName={cn("h-8 font-bold text-center uppercase px-2", !ticker && "border-destructive focus:border-destructive")}
              />
              {!ticker && <span className="text-destructive">*</span>}
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
              <Select
                value={decision}
                onValueChange={(val) => {
                  const newDecision = val as DecisionType;
                  setDecision(newDecision);
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      editor.update(() => {
                        if (newDecision !== prevValuesRef.current.decision) {
                          node.setDecision(newDecision);
                          prevValuesRef.current.decision = newDecision;
                        }
                      });
                    });
                  });
                }}
              >
                <SelectTrigger className="h-8 w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DECISION_TYPE.BUY}>{t("editor.decisionDetail.buy" as any)}</SelectItem>
                  <SelectItem value={DECISION_TYPE.SELL}>{t("editor.decisionDetail.sell" as any)}</SelectItem>
                  <SelectItem value={DECISION_TYPE.HOLD}>{t("editor.decisionDetail.hold" as any)}</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-destructive">*</span>
              <Select
                value={actionType}
                onValueChange={(val) => {
                  const newActionType = val as DecisionActionType;
                  setActionType(newActionType);
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      editor.update(() => {
                        if (newActionType !== prevValuesRef.current.actionType) {
                          node.setActionType(newActionType);
                          prevValuesRef.current.actionType = newActionType;
                        }
                      });
                    });
                  });
                }}
              >
                <SelectTrigger
                  className={cn(
                    "h-8 w-28",
                    actionType === DECISION_ACTION_TYPE.FOLLOW
                      ? "border-green-500 text-green-700 dark:text-green-400"
                      : actionType === DECISION_ACTION_TYPE.OVERRIDE
                        ? "border-red-500 text-red-700 dark:text-red-400"
                        : "border-muted-foreground text-muted-foreground",
                  )}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DECISION_ACTION_TYPE.NONE}>
                    <div className="flex items-center gap-2">
                      <FileText className="w-3 h-3 text-muted-foreground" />
                      {t("editor.decisionDetail.none" as any)}
                    </div>
                  </SelectItem>
                  <SelectItem value={DECISION_ACTION_TYPE.FOLLOW}>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                      {t("editor.decisionDetail.follow" as any)}
                    </div>
                  </SelectItem>
                  <SelectItem value={DECISION_ACTION_TYPE.OVERRIDE}>
                    <div className="flex items-center gap-2">
                      <FileText className="w-3 h-3 text-red-600" />
                      {t("editor.decisionDetail.override" as any)}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <span className="text-destructive">*</span>
            </div>
            <NodeDeleteButton onClick={handleDelete} title={t("editor.decisionDetail.delete" as any)} variant="hover-show" />
          </div>

          {/* Body */}
          <div className="p-4 space-y-4">
            <div className="flex gap-3">
              <div className="flex-1">
                <Label htmlFor="price" className="text-sm font-medium mb-2 block">
                  {t("editor.decisionDetail.price" as any)}
                  <span className="text-destructive ml-1">*</span>
                </Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={price || ""}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setPrice(val);
                    updatePrice(val);
                  }}
                  placeholder="0.00"
                  className={cn("h-9", price < 0 && "border-destructive focus:border-destructive")}
                />
                {price < 0 && <p className="text-xs text-destructive mt-1">{t("editor.decisionDetail.price-negative" as any)}</p>}
              </div>
              <div className="flex-1">
                <Label htmlFor="quantity" className="text-sm font-medium mb-2 block">
                  {t("editor.decisionDetail.quantity" as any)}
                  <span className="text-destructive ml-1">*</span>
                </Label>
                <Input
                  id="quantity"
                  type="number"
                  step="1"
                  value={quantity || ""}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setQuantity(val);
                    updateQuantity(val);
                  }}
                  placeholder="0"
                  className={cn("h-9", quantity < 0 && "border-destructive focus:border-destructive")}
                />
                {quantity < 0 && <p className="text-xs text-destructive mt-1">{t("editor.decisionDetail.quantity-negative" as any)}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="contractId" className="text-sm font-medium block">
                  {t("editor.decisionDetail.linkContract" as any)}
                </Label>
                {!showContractSearch && !showContractRecommendation && (
                  <button
                    type="button"
                    onClick={handleOpenContractSearch}
                    className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
                  >
                    {t("editor.decisionDetail.searchContract" as any)}
                  </button>
                )}
              </div>

              {contractId ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 px-3 py-2 bg-primary/10 border border-primary/20 rounded-md text-sm">
                    <span className="font-medium text-primary">{contractId}</span>
                    {contractName && (
                      <>
                        <span className="text-muted-foreground">- {contractName}</span>
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleClearContract}
                    className="h-9 w-9 flex items-center justify-center rounded-md border border-muted-foreground/20 hover:bg-destructive/10 hover:text-destructive transition-colors"
                    title="取消关联"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div
                  onClick={handleOpenContractSearch}
                  className="px-3 py-2 bg-muted/30 border border-dashed border-muted-foreground/30 rounded-md text-sm text-muted-foreground cursor-pointer hover:bg-muted/50 hover:border-primary/30 transition-colors flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  <span>{t("editor.decisionDetail.clickToSelect" as any)}</span>
                </div>
              )}

              {showContractSearch && (
                <ContractSearch preferredTicker={ticker} onSelectContract={handleSelectContract} onClose={handleCloseContractSearch} />
              )}

              {showContractRecommendation && !showContractSearch && !contractId && (
                <ContractRecommendation
                  ticker={ticker}
                  onSelectContract={handleSelectContract}
                  onSkip={handleSkipRecommendation}
                  onOpenSearch={handleOpenContractSearch}
                />
              )}
            </div>

            <div>
              <Label htmlFor="reason" className="text-sm font-medium flex items-center gap-1 mb-2">
                <FileText className="w-3 h-3" />
                {t("editor.decisionDetail.reason" as any)}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  updateReason(e.target.value);
                }}
                placeholder={t("editor.decisionDetail.reasonPlaceholder" as any)}
                className={cn("min-h-[60px] resize-none", !reason && "border-destructive focus:border-destructive")}
                rows={2}
              />
              {!reason && <p className="text-xs text-destructive mt-1">{t("editor.decisionDetail.reason-required" as any)}</p>}
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor="executedAt" className="text-sm font-medium w-24 flex items-center gap-1 flex-shrink-0">
                {t("editor.decisionDetail.executedAt" as any)}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="executedAt"
                type="datetime-local"
                value={executedAt ? new Date(executedAt).toISOString().slice(0, 16) : ""}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val) {
                    const date = new Date(val);
                    const isoString = date.toISOString();
                    setExecutedAt(isoString);
                    updateExecutedAt(isoString);
                  }
                }}
                className={cn("flex-1", !executedAt && "border-destructive")}
              />
            </div>
          </div>
        </div>
      )}
    </WindowDecorator>
  );
}

// =============================================================================
// Factory Functions
// =============================================================================

export function $createDecisionNode(data?: Partial<DecisionData>): DecisionNode {
  const node = new DecisionNode(
    data?.ticker,
    data?.contractId,
    data?.decision,
    data?.actionType,
    data?.price,
    data?.quantity,
    data?.reason,
    data?.executedAt || new Date().toISOString(),
    data?.createdAt,
    data?.confidence,
    data?.riskLevel,
    data?.timeHorizon,
  );
  return $applyNodeReplacement(node);
}
